import { useEffect, useState } from "react";
import { BookOpenCheck, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DeveloperCredit } from "@/components/DeveloperCredit";
import { InstallCard } from "@/components/InstallCard";
import { usePrefs } from "@/context/PrefsContext";
import { api } from "@/lib/api";

function Section({ s, open, onToggle }) {
  return (
    <div className="soft-card overflow-hidden" data-testid={`guide-section-${s.id}`}>
      <button data-testid={`guide-section-toggle-${s.id}`} onClick={onToggle} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-accent/60">
        <div><h3 className="text-base font-semibold">{s.title}</h3><p className="mt-0.5 text-sm text-muted-foreground">{s.desc}</p></div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="space-y-3 border-t border-border px-6 py-5">
          {s.steps.map((st, i) => <li key={st.slice ? st.slice(0, 40) : i} className="flex gap-3 text-sm leading-relaxed"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">{i + 1}</span><span>{st}</span></li>)}
        </ol>
      )}
    </div>
  );
}

export default function Guide() {
  const { user } = useAuth();
  const { t } = usePrefs();
  const [g, setG] = useState(null);
  const [open, setOpen] = useState("install");
  const [showLog, setShowLog] = useState(false);
  useEffect(() => { api.get("/guide").then((r) => setG(r.data)); }, []);
  if (!g) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  const isAdmin = user?.role === "superadmin";
  const sections = g.sections.filter((s) => s.audience === "student" || isAdmin);

  return (
    <div className="mx-auto max-w-2xl space-y-6 fade-up" data-testid="guide-page">
      <div>
        <p className="font-jpserif text-sm tracking-[0.3em] text-muted-foreground">ガイド</p>
        <h1 className="mt-3 font-jpserif text-3xl sm:text-4xl">Panduan Sistem</h1>
        <p className="mt-3 text-sm text-muted-foreground">Cara mengoperasikan YUTAKA MANABI dan penjelasan setiap fitur. Panduan ini diperbarui otomatis bersamaan dengan setiap pembaruan aplikasi.</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs" data-testid="guide-version"><BookOpenCheck className="h-3.5 w-3.5" />v{g.version} · diperbarui {g.updated_at}</div>
      </div>

      <InstallCard />

      <div className="space-y-3">
        {sections.map((s) => <Section key={s.id} s={s} open={open === s.id} onToggle={() => setOpen(open === s.id ? null : s.id)} />)}
      </div>

      <div className="soft-card p-6" data-testid="guide-changelog">
        <button data-testid="guide-changelog-toggle" onClick={() => setShowLog(!showLog)} className="flex min-h-[44px] w-full items-center justify-between text-left"><span className="text-sm font-semibold">Riwayat pembaruan</span><ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showLog ? "rotate-180" : ""}`} /></button>
        {showLog && g.changelog.map((c) => (
          <div key={c.version} className="mt-5 border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">{c.date} · v{c.version}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{c.items.map((it, i) => <li key={it.slice(0, 40) + i}>{it}</li>)}</ul>
          </div>
        ))}
      </div>

      <div className="soft-card p-6" data-testid="guide-about">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Tentang aplikasi</p>
        <h3 className="mt-1 text-base font-semibold">YUTAKA MANABI — 豊か学び</h3>
        <div className="mt-3"><DeveloperCredit testid="guide-developer-credit" /></div>
      </div>
    </div>
  );
}
